// Shim for next/navigation. Maps the legacy Next API surface (`useRouter`,
// `usePathname`, `useSearchParams`, `redirect`, `notFound`) onto the
// equivalent react-router primitives so component code keeps compiling
// unchanged.
//
// Notes:
// - `redirect()` throws a sentinel — in React Router you'd use a loader.
//   Pages calling redirect() at render time are converted page-by-page; for
//   now we navigate with window.location.assign which is good enough for
//   client-side redirects.
// - `notFound()` is a no-op stub.

import { useNavigate, useLocation, useSearchParams as useRRSearchParams } from 'react-router';

interface RouterOptions {
    scroll?: boolean;
}

export function useRouter() {
    const navigate = useNavigate();
    return {
        push: (href: string, _opts?: RouterOptions) => navigate(href),
        replace: (href: string, _opts?: RouterOptions) => navigate(href, { replace: true }),
        back: () => navigate(-1),
        forward: () => navigate(1),
        refresh: () => {
            if (typeof window !== 'undefined') window.location.reload();
        },
        prefetch: (_href: string) => {
            // No-op — Vite splits chunks lazily and react-router handles prefetch.
        },
    };
}

export function usePathname(): string {
    const location = useLocation();
    return location.pathname;
}

export function useSearchParams(): URLSearchParams {
    const [params] = useRRSearchParams();
    return params;
}

export function useParams<T extends Record<string, string>>(): T {
    // React Router's useParams ships with the same shape; re-export to keep
    // call-sites stable.
    // Lazy import to avoid a circular layer.
    // eslint-disable-next-line @typescript-eslint/no-require-imports -- deliberate lazy require breaks a circular import between this shim and react-router
    const reactRouter = require('react-router') as typeof import('react-router');
    return reactRouter.useParams() as T;
}

export function redirect(href: string): never {
    if (typeof window !== 'undefined') {
        window.location.assign(href);
    }
    throw new Error(`redirect(${href})`);
}

export function notFound(): never {
    throw new Error('notFound() called');
}

export function permanentRedirect(href: string): never {
    return redirect(href);
}
